import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { type ApiError } from "../api/http";
import { buildQueryString } from "../utils/queryString";
import type {
  Notification,
  NotificationListParams,
  NotificationPreference,
  PaginatedResponse,
  UnreadCountResponse,
} from "../api/types";

export const notificationKeys = {
  list: (params: NotificationListParams) => ["notifications", params] as const,
  detail: (id: number) => ["notifications", id] as const,
  unreadCount: () => ["notifications", "unread-count"] as const,
  preferences: () => ["notifications", "preferences"] as const,
};

export function useNotifications(params: NotificationListParams = {}) {
  return useQuery<PaginatedResponse<Notification>, ApiError>({
    queryKey: notificationKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<Notification>>(
        `/api/v1/notifications/${buildQueryString(params as Record<string, string | number | undefined>)}`
      ),
    refetchInterval: 30_000,
  });
}

export function useUnreadCount() {
  return useQuery<UnreadCountResponse, ApiError>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () =>
      api.get<UnreadCountResponse>("/api/v1/notifications/unread-count/"),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation<Notification, ApiError, number>({
    mutationFn: (id) =>
      api.post<Notification>(`/api/v1/notifications/${id}/mark-read/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean; updated: number }, ApiError, void>({
    mutationFn: () =>
      api.post<{ ok: boolean; updated: number }>(
        "/api/v1/notifications/mark-all-read/"
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery<NotificationPreference, ApiError>({
    queryKey: notificationKeys.preferences(),
    queryFn: () =>
      api.get<NotificationPreference>("/api/v1/notifications/preferences/"),
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationPreference,
    ApiError,
    Partial<Pick<NotificationPreference, "preferences" | "email_enabled">>
  >({
    mutationFn: (data) =>
      api.patch<NotificationPreference>(
        "/api/v1/notifications/preferences/",
        data
      ),
    onSuccess: (prefs) => {
      queryClient.setQueryData(notificationKeys.preferences(), prefs);
    },
  });
}
